import React, { useEffect } from 'react';
import { FaUserCheck, FaHandshake, FaBullhorn, FaChartLine, FaUsers, FaSearchDollar } from 'react-icons/fa';
import AOS from 'aos';
import 'aos/dist/aos.css';

const Influencing = () => {
  const features = [
    {
      icon: <FaUserCheck />,
      title: "Influencer Matching",
      description: "Connect with relevant influencers who align with your brand values and target audience"
    },
    {
      icon: <FaHandshake />,
      title: "Partnership Management",
      description: "End-to-end management of influencer relationships and campaign coordination"
    },
    {
      icon: <FaBullhorn />,
      title: "Content Strategy",
      description: "Develop engaging content strategies that resonate with your target audience"
    },
    {
      icon: <FaChartLine />,
      title: "Performance Tracking",
      description: "Real-time monitoring and analytics of campaign performance and engagement"
    },
    {
      icon: <FaUsers />,
      title: "Audience Analysis",
      description: "In-depth analysis of audience demographics and engagement patterns"
    },
    {
      icon: <FaSearchDollar />,
      title: "ROI Optimization",
      description: "Maximize return on investment through data-driven campaign optimization"
    }
  ];

  const processSteps = [
    {
      step: 1,
      title: "Discovery & Strategy",
      description: "Understanding your brand and defining campaign objectives",
      details: [
        "Brand analysis and goal setting",
        "Target audience identification",
        "Campaign strategy development",
        "Budget planning and allocation"
      ]
    },
    {
      step: 2,
      title: "Influencer Selection",
      description: "Finding the perfect influencer match for your brand",
      details: [
        "Influencer research and vetting",
        "Audience alignment analysis",
        "Engagement rate evaluation",
        "Brand value compatibility check"
      ]
    },
    {
      step: 3,
      title: "Campaign Execution",
      description: "Managing and implementing influencer campaigns",
      details: [
        "Content brief development",
        "Campaign timeline management",
        "Content review and approval",
        "Real-time campaign monitoring"
      ]
    },
    {
      step: 4,
      title: "Analysis & Optimization",
      description: "Measuring success and optimizing performance",
      details: [
        "Performance metrics tracking",
        "Engagement analysis",
        "ROI measurement",
        "Campaign optimization"
      ]
    }
  ];

  const benefits = [
    {
      stat: "85%",
      label: "Brand Trust Increase"
    },
    {
      stat: "3x",
      label: "Engagement Rate"
    },
    {
      stat: "65%",
      label: "Lead Generation Growth"
    },
    {
      stat: "90%",
      label: "Client Satisfaction"
    }
  ];

  useEffect(() => {
    AOS.init({ duration: 1000, once: true, easing: 'ease-out' });
  }, []);

  const services = [
    {
      icon: <FaUserCheck className="w-8 h-8" />, 
      title: "Influencer Matching",
      description: "Connect with relevant influencers who align with your brand values and target audience.",
      color: "bg-blue-50",
      iconColor: "text-blue-600",
      borderColor: "border-blue-200"
    },
    {
      icon: <FaHandshake className="w-8 h-8" />, 
      title: "Partnership Management",
      description: "End-to-end management of influencer relationships and campaign coordination.",
      color: "bg-purple-50",
      iconColor: "text-purple-600",
      borderColor: "border-purple-200"
    },
    {
      icon: <FaBullhorn className="w-8 h-8" />, 
      title: "Content Strategy",
      description: "Develop engaging content strategies that resonate with your target audience.",
      color: "bg-green-50",
      iconColor: "text-green-600",
      borderColor: "border-green-200"
    },
    {
      icon: <FaChartLine className="w-8 h-8" />, 
      title: "Performance Tracking",
      description: "Real-time monitoring and analytics of campaign performance and engagement.",
      color: "bg-red-50",
      iconColor: "text-red-600",
      borderColor: "border-red-200"
    },
    {
      icon: <FaUsers className="w-8 h-8" />, 
      title: "Audience Analysis",
      description: "In-depth analysis of audience demographics and engagement patterns.",
      color: "bg-yellow-50",
      iconColor: "text-yellow-600",
      borderColor: "border-yellow-200"
    },
    {
      icon: <FaSearchDollar className="w-8 h-8" />, 
      title: "ROI Optimization",
      description: "Maximize return on investment through data-driven campaign optimization.",
      color: "bg-indigo-50",
      iconColor: "text-indigo-600",
      borderColor: "border-indigo-200"
    }
  ];

  const benefitsData = [
    { title: "Brand Trust Increase", value: "85", suffix: "%" },
    { title: "Engagement Rate", value: "3", suffix: "x" },
    { title: "Lead Generation Growth", value: "65", suffix: "%" },
    { title: "Client Satisfaction", value: "90", suffix: "%" }
  ];

  return (
    <div className="min-h-screen pt-20 px-4 bg-gradient-to-br from-gray-50 via-white to-purple-50">
      <div className="max-w-7xl mx-auto">
        {/* Hero Section */}
        <div className="text-center mt-24 mb-16" data-aos="fade-up">
          <div className="relative inline-block">
            <h1 className="text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
              Influencer Marketing Services
            </h1>
            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full"></div>
          </div>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mt-8 leading-relaxed">
            Connect with influential voices and amplify your brand message through strategic influencer partnerships and campaigns.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {services.map((service, index) => (
            <div
              key={index}
              data-aos="fade-up"
              data-aos-delay={index * 100}
              className={`${service.color} rounded-xl p-6 transform hover:-translate-y-2 transition-all duration-300 hover:shadow-xl border ${service.borderColor}`}
            >
              <div className={`${service.iconColor} mb-6 bg-white p-4 rounded-lg inline-block shadow-md`}>
                {service.icon}
              </div>
              <h3 className="text-2xl font-semibold mb-4">{service.title}</h3>
              <p className="text-gray-600 leading-relaxed">{service.description}</p>
            </div>
          ))}
        </div>

        {/* Benefits Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-16" data-aos="fade-up">
          <h2 className="text-3xl font-bold text-center mb-12">Our Impact</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {benefitsData.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-purple-600 mb-2">
                  {benefit.value}{benefit.suffix}
                </div>
                <p className="text-gray-600">{benefit.title}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div 
          className="relative overflow-hidden rounded-2xl mb-16"
          data-aos="fade-up"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 opacity-90"></div>
          <div className="relative z-10 p-12 text-center text-white">
            <h2 className="text-3xl font-bold mb-6">Ready to Amplify Your Brand?</h2>
            <p className="text-xl mb-8 opacity-90">Start your influencer journey with us today</p>
            <div className="flex gap-4 justify-center">
              <button className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-opacity-90 transition-all duration-300 shadow-lg">
                Get Started
              </button>
              <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300">
                Learn More
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Influencing;
